import { useMemo, useState } from "react";
import {
  ArrowLeft,
  Building2,
  Calculator,
  ExternalLink,
  FileSpreadsheet,
  Info,
  Landmark,
  RotateCcw,
  Scale,
  Users,
} from "lucide-react";

type Mode = "rental" | "sale";
type Phase = [number, number, number];
type Costs = {
  avgHomeGsf: number;
  homeHardPsf: number;
  commonsGsf: number;
  commonsHardPsf: number;
  land: number;
  closing: number;
  grading: number;
  roads: number;
  wetUtilities: number;
  landscape: number;
  resilience: number;
  aePct: number;
  entitlement: number;
  permitsPerHome: number;
  ownerCosts: number;
  commonFfe: number;
  techPerHome: number;
  mobility: number;
  marketingOpening: number;
  recruiting: number;
  operatingReserve: number;
  finance: number;
  contingencyPct: number;
  developerFeePct: number;
};
type Economics = {
  monthlyRent: number;
  ancillaryRent: number;
  occupancy: number;
  capRate: number;
  salePrice: number;
  serviceFee: number;
  groundRent: number;
  salesCostPct: number;
  groundCapRate: number;
  absorption: number;
  buyerDownPct: number;
  mortgageRate: number;
  mortgageTerm: number;
  ownerInsuranceMonth: number;
  constructionDebtPct: number;
  payrollBurden: number;
  residentsPerHome: number;
  foodPerResidentDay: number;
  utilitiesPerHomeMonth: number;
  commonUtilities: number;
  maintenancePct: number;
  insurance: number;
  programmingPerHomeMonth: number;
  transport: number;
  softwareAdmin: number;
  reservePerHomeMonth: number;
  managementFeePct: number;
  rentalMarketingPct: number;
  propertyTaxPct: number;
};
type Staff = { role: string; department: string; fte: number; salary: number };

const usd = (v: number, d = 0) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: d,
  }).format(v);
const compact = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(v);
const pct = (v: number) => `${v.toFixed(1)}%`;

const defaultCosts: Costs = {
  avgHomeGsf: 950,
  homeHardPsf: 310,
  commonsGsf: 38000,
  commonsHardPsf: 450,
  land: 4.2,
  closing: 0.6,
  grading: 4.5,
  roads: 3.2,
  wetUtilities: 3,
  landscape: 2,
  resilience: 1.8,
  aePct: 7.5,
  entitlement: 2,
  permitsPerHome: 18000,
  ownerCosts: 2,
  commonFfe: 3.5,
  techPerHome: 8000,
  mobility: 2.2,
  marketingOpening: 2,
  recruiting: 1,
  operatingReserve: 3,
  finance: 9,
  contingencyPct: 8,
  developerFeePct: 4,
};
const defaultEconomics: Economics = {
  monthlyRent: 8000,
  ancillaryRent: 0,
  occupancy: 90,
  capRate: 6.5,
  salePrice: 950000,
  serviceFee: 3500,
  groundRent: 350,
  salesCostPct: 5,
  groundCapRate: 6.5,
  absorption: 3.5,
  buyerDownPct: 40,
  mortgageRate: 6.75,
  mortgageTerm: 30,
  ownerInsuranceMonth: 200,
  constructionDebtPct: 60,
  payrollBurden: 30,
  residentsPerHome: 1.2,
  foodPerResidentDay: 15,
  utilitiesPerHomeMonth: 250,
  commonUtilities: 250000,
  maintenancePct: 0.6,
  insurance: 500000,
  programmingPerHomeMonth: 100,
  transport: 250000,
  softwareAdmin: 300000,
  reservePerHomeMonth: 250,
  managementFeePct: 5,
  rentalMarketingPct: 2,
  propertyTaxPct: 1.1,
};
const defaultStaff: Staff[] = [
  {
    role: "Executive director",
    department: "Leadership",
    fte: 1,
    salary: 165000,
  },
  {
    role: "Operations, finance & concierge",
    department: "Administration",
    fte: 4,
    salary: 72000,
  },
  {
    role: "RN / wellness team",
    department: "Wellness",
    fte: 2,
    salary: 125000,
  },
  {
    role: "Care coordinators & 24/7 response",
    department: "Wellness",
    fte: 4,
    salary: 65000,
  },
  {
    role: "Culinary & dining",
    department: "Hospitality",
    fte: 14,
    salary: 50000,
  },
  {
    role: "Housekeeping & laundry",
    department: "Hospitality",
    fte: 9,
    salary: 46000,
  },
  {
    role: "Culture, fitness & activities",
    department: "Life enrichment",
    fte: 5,
    salary: 60000,
  },
  {
    role: "Security & resident concierge",
    department: "Resident services",
    fte: 5,
    salary: 54000,
  },
  {
    role: "Facilities & grounds",
    department: "Plant operations",
    fte: 5,
    salary: 60000,
  },
  {
    role: "Mobility & delivery operations",
    department: "Transportation",
    fte: 3,
    salary: 56000,
  },
];

function NumberField({
  label,
  value,
  step = 1,
  min = 0,
  max,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="ledger-input">
      <span>{label}</span>
      <div>
        <input
          type="number"
          value={value}
          step={step}
          min={min}
          max={max}
          onChange={(e) =>
            onChange(
              Math.max(
                min,
                Math.min(
                  max ?? Number.POSITIVE_INFINITY,
                  Number(e.target.value),
                ),
              ),
            )
          }
        />
        {suffix && <small>{suffix}</small>}
      </div>
    </label>
  );
}

export default function DeveloperV2() {
  const [mode, setMode] = useState<Mode>("rental");
  const [phaseHomes, setPhaseHomes] = useState<Phase>([80, 50, 50]);
  const [costs, setCosts] = useState(defaultCosts);
  const [econ, setEcon] = useState(defaultEconomics);
  const [staff, setStaff] = useState(defaultStaff);
  const totalHomes = phaseHomes.reduce((a, b) => a + b, 0);
  const setCost = (key: keyof Costs, value: number) =>
    setCosts((c) => ({ ...c, [key]: value }));
  const setE = (key: keyof Economics, value: number) =>
    setEcon((c) => ({ ...c, [key]: value }));

  const model = useMemo(() => {
    const m = 1_000_000;
    const residential = totalHomes * costs.avgHomeGsf * costs.homeHardPsf;
    const commons = costs.commonsGsf * costs.commonsHardPsf;
    const site =
      (costs.grading +
        costs.roads +
        costs.wetUtilities +
        costs.landscape +
        costs.resilience) *
      m;
    const ae = ((residential + commons + site) * costs.aePct) / 100;
    const permits = totalHomes * costs.permitsPerHome;
    const tech = totalHomes * costs.techPerHome;
    const directBeforeContingency = residential + commons + site;
    const contingency = (directBeforeContingency * costs.contingencyPct) / 100;
    const preFee =
      (costs.land +
        costs.closing +
        costs.entitlement +
        costs.ownerCosts +
        costs.commonFfe +
        costs.mobility +
        costs.marketingOpening +
        costs.recruiting +
        costs.operatingReserve +
        costs.finance) *
        m +
      residential +
      commons +
      site +
      ae +
      permits +
      tech +
      contingency;
    const developerFee = (preFee * costs.developerFeePct) / 100;
    const totalCost = preFee + developerFee;

    const payrollBase = staff.reduce(
      (sum, row) => sum + row.fte * row.salary,
      0,
    );
    const payroll = payrollBase * (1 + econ.payrollBurden / 100);
    const occupiedHomes = (totalHomes * econ.occupancy) / 100;
    const food =
      occupiedHomes * econ.residentsPerHome * 365 * econ.foodPerResidentDay;
    const utilities =
      totalHomes * econ.utilitiesPerHomeMonth * 12 + econ.commonUtilities;
    const maintenance = ((residential + commons) * econ.maintenancePct) / 100;
    const programming = totalHomes * econ.programmingPerHomeMonth * 12;
    const reserves = totalHomes * econ.reservePerHomeMonth * 12;
    const serviceRevenue = totalHomes * econ.serviceFee * 12;
    const management = (serviceRevenue * econ.managementFeePct) / 100;
    const serviceCostsBeforeManagement =
      payroll +
      food +
      utilities +
      maintenance +
      econ.insurance +
      programming +
      econ.transport +
      econ.softwareAdmin +
      reserves;
    const serviceCosts = serviceCostsBeforeManagement + management;
    const breakEvenService =
      serviceCostsBeforeManagement /
      totalHomes /
      12 /
      Math.max(1 - econ.managementFeePct / 100, 0.01);
    const serviceSurplus = serviceRevenue - serviceCosts;
    const serviceCoverage = serviceRevenue / serviceCosts;

    const rentalRevenue =
      occupiedHomes * 12 * (econ.monthlyRent + econ.ancillaryRent);
    const rentalPropertyTax =
      ((costs.land * m + residential + commons + site) * econ.propertyTaxPct) /
      100;
    const rentalMarketing = (rentalRevenue * econ.rentalMarketingPct) / 100;
    const rentalOpex =
      serviceCosts - management + rentalPropertyTax + rentalMarketing;
    const rentalNoi = rentalRevenue - rentalOpex;
    const rentalValue = rentalNoi / Math.max(econ.capRate / 100, 0.0001);
    const rentalMargin = rentalValue - totalCost;
    const grossSales = totalHomes * econ.salePrice;
    const sellingCosts = (grossSales * econ.salesCostPct) / 100;
    const saleProfit = grossSales - sellingCosts - totalCost;
    const annualGroundRent = totalHomes * econ.groundRent * 12;
    const groundValue =
      annualGroundRent / Math.max(econ.groundCapRate / 100, 0.0001);
    const sponsorRecurring = annualGroundRent + management;
    const selloutMonths = totalHomes / Math.max(econ.absorption, 0.25);
    const constructionLoan = (totalCost * econ.constructionDebtPct) / 100;
    const sponsorEquity = totalCost - constructionLoan;
    const buyerDownPayment = (econ.salePrice * econ.buyerDownPct) / 100;
    const buyerLoan = econ.salePrice - buyerDownPayment;
    const mortgageMonthlyRate = econ.mortgageRate / 100 / 12;
    const mortgagePayments = Math.max(econ.mortgageTerm * 12, 1);
    const buyerPrincipalInterest =
      mortgageMonthlyRate === 0
        ? buyerLoan / mortgagePayments
        : buyerLoan *
          (mortgageMonthlyRate /
            (1 - Math.pow(1 + mortgageMonthlyRate, -mortgagePayments)));
    const buyerPropertyTax = (econ.salePrice * econ.propertyTaxPct) / 100 / 12;
    const buyerMonthlyCarry =
      buyerPrincipalInterest +
      buyerPropertyTax +
      econ.ownerInsuranceMonth +
      econ.serviceFee +
      econ.groundRent;

    const costRows = [
      ["Land acquisition", costs.land * m, "Fixed", "100% at closing"],
      [
        "Closing, diligence & title",
        costs.closing * m,
        "Fixed",
        "Predevelopment",
      ],
      [
        "Residential shells + interiors",
        residential,
        `${totalHomes} × ${costs.avgHomeGsf} GSF × ${usd(costs.homeHardPsf)}`,
        "By home",
      ],
      [
        "Commons, kitchen, wellness + mandir",
        commons,
        `${costs.commonsGsf.toLocaleString()} GSF × ${usd(costs.commonsHardPsf)}`,
        "80% Phase 1",
      ],
      ["Grading & earthwork", costs.grading * m, "Allowance", "65% Phase 1"],
      [
        "Roads, parking & fire lanes",
        costs.roads * m,
        "Allowance",
        "65% Phase 1",
      ],
      [
        "Water, sewer & wet utilities",
        costs.wetUtilities * m,
        "Allowance",
        "65% Phase 1",
      ],
      [
        "Landscape, gardens & recreation",
        costs.landscape * m,
        "Allowance",
        "65% Phase 1",
      ],
      [
        "Solar, battery, EV & resilience",
        costs.resilience * m,
        "Allowance",
        "65% Phase 1",
      ],
      [
        "Architecture & engineering",
        ae,
        `${costs.aePct}% of direct hard cost`,
        "By phase",
      ],
      [
        "Entitlement, CEQA & legal",
        costs.entitlement * m,
        "Fixed",
        "Predevelopment",
      ],
      [
        "Permits & impact fees",
        permits,
        `${usd(costs.permitsPerHome)}/home`,
        "By home",
      ],
      [
        "Owner representative & project costs",
        costs.ownerCosts * m,
        "Fixed",
        "Across phases",
      ],
      [
        "Common FF&E + production kitchen",
        costs.commonFfe * m,
        "Fixed",
        "80% Phase 1",
      ],
      [
        "In-home safety + resident technology",
        tech,
        `${usd(costs.techPerHome)}/home`,
        "By home",
      ],
      [
        "Mobility fleet, depot & charging",
        costs.mobility * m,
        "Fixed",
        "60% Phase 1",
      ],
      [
        "Marketing, model homes & opening",
        costs.marketingOpening * m,
        "Fixed",
        "Front-loaded",
      ],
      [
        "Recruitment, training & uniforms",
        costs.recruiting * m,
        "Fixed",
        "Before opening",
      ],
      [
        "Operating / HOA opening reserve",
        costs.operatingReserve * m,
        "Fixed",
        "Before opening",
      ],
      [
        "Construction interest & lender fees",
        costs.finance * m,
        "Allowance",
        "Across phases",
      ],
      [
        "Construction contingency",
        contingency,
        `${costs.contingencyPct}% of direct hard cost`,
        "Held by phase",
      ],
      [
        "Developer fee",
        developerFee,
        `${costs.developerFeePct}% of pre-fee uses`,
        "Across phases",
      ],
    ] as const;

    const ratios = phaseHomes.map((n) => n / totalHomes);
    const phaseCosts = phaseHomes.map((homes, i) => {
      const direct =
        homes *
        (costs.avgHomeGsf * costs.homeHardPsf +
          costs.permitsPerHome +
          costs.techPerHome);
      const commonShare = [0.8, 0.1, 0.1][i];
      const siteShare = [0.65, 0.2, 0.15][i];
      const openShare = [0.6, 0.2, 0.2][i];
      const financeShare = [0.5, 0.25, 0.25][i];
      const mobilityShare = [0.6, 0.2, 0.2][i];
      const allocated =
        direct +
        commons * commonShare +
        site * siteShare +
        ae * ratios[i] +
        (costs.land + costs.closing + costs.entitlement) *
          m *
          (i === 0 ? 1 : 0) +
        costs.ownerCosts * m * [0.45, 0.3, 0.25][i] +
        costs.commonFfe * m * commonShare +
        costs.mobility * m * mobilityShare +
        (costs.marketingOpening + costs.recruiting + costs.operatingReserve) *
          m *
          openShare +
        costs.finance * m * financeShare +
        contingency * ratios[i];
      return allocated * (1 + costs.developerFeePct / 100);
    });
    const phaseData = phaseHomes.map((homes, i) => ({
      phase: `Phase ${i + 1}`,
      homes,
      cost: phaseCosts[i],
      cumulative: phaseCosts.slice(0, i + 1).reduce((a, b) => a + b, 0),
      sales: homes * econ.salePrice * (1 - econ.salesCostPct / 100),
      annualRent:
        ((homes * econ.occupancy) / 100) *
        12 *
        (econ.monthlyRent + econ.ancillaryRent),
    }));
    return {
      residential,
      commons,
      site,
      ae,
      contingency,
      developerFee,
      totalCost,
      costRows,
      payrollBase,
      payroll,
      food,
      utilities,
      maintenance,
      programming,
      reserves,
      management,
      serviceCosts,
      serviceRevenue,
      serviceSurplus,
      serviceCoverage,
      breakEvenService,
      rentalRevenue,
      rentalOpex,
      rentalNoi,
      rentalValue,
      rentalMargin,
      grossSales,
      sellingCosts,
      saleProfit,
      annualGroundRent,
      groundValue,
      sponsorRecurring,
      selloutMonths,
      constructionLoan,
      sponsorEquity,
      buyerDownPayment,
      buyerLoan,
      buyerPrincipalInterest,
      buyerPropertyTax,
      buyerMonthlyCarry,
      phaseData,
    };
  }, [costs, econ, phaseHomes, staff, totalHomes]);

  const reset = () => {
    setPhaseHomes([80, 50, 50]);
    setCosts(defaultCosts);
    setEcon(defaultEconomics);
    setStaff(defaultStaff);
  };
  const activeProfit =
    mode === "rental" ? model.rentalMargin : model.saleProfit;
  return (
    <div className="developer-page developer-v2">
      <header className="dev-header">
        <a href="/" className="dev-back">
          <ArrowLeft /> Main report
        </a>
        <div className="dev-title">
          <span className="dev-monogram">अ</span>
          <div>
            <strong>Ananda Village</strong>
            <small>Capital + operating ledger</small>
          </div>
        </div>
        <nav>
          <a href="#compare">Models</a>
          <a href="#ledger">Costs</a>
          <a href="#staff">People</a>
          <a href="#phasing">Phasing</a>
          <a href="#lot">Lot + plans</a>
        </nav>
      </header>
      <main>
        <section className="dev-hero ledger-hero">
          <div>
            <p>Developer edition · live nominal 2026 feasibility model</p>
            <h1>Two ways to build the same beautiful life.</h1>
            <span>
              Rent the complete home and service for about $8,000 a month—or
              sell the residence on sponsor-retained land and fund the
              experience through a $3,000–$4,000 monthly service assessment.
            </span>
          </div>
          <div
            className={`dev-verdict ${activeProfit >= 0 ? "pencils" : "gap"}`}
          >
            <small>
              Selected case ·{" "}
              {mode === "rental"
                ? "sponsor-owned rental"
                : "leasehold home sale"}
            </small>
            <strong>
              {activeProfit >= 0 ? "Creates value" : "Needs redesign"}
            </strong>
            <p>
              {mode === "rental"
                ? `${compact(model.rentalMargin)} stabilized value less cost.`
                : `${compact(model.saleProfit)} sellout profit before tax and promote; retained land value shown separately.`}
            </p>
          </div>
        </section>
        <section className="dev-toolbar">
          <div className="dev-presets">
            <button
              className={mode === "rental" ? "active" : ""}
              onClick={() => setMode("rental")}
            >
              Model A · $8K rental
            </button>
            <button
              className={mode === "sale" ? "active" : ""}
              onClick={() => setMode("sale")}
            >
              Model B · sold homes + service HOA
            </button>
          </div>
          <button className="dev-reset" onClick={reset}>
            <RotateCcw /> Reset assumptions
          </button>
        </section>

        <section className="model-compare" id="compare">
          <article className={mode === "rental" ? "selected" : ""}>
            <span>MODEL A</span>
            <h2>Sponsor-owned rental</h2>
            <p>
              The resident pays one recurring monthly fee. The sponsor owns
              land, residences and commons, carries the full development basis,
              and earns real-estate NOI.
            </p>
            <dl>
              <div>
                <dt>Resident entry</dt>
                <dd>Deposit / community fee</dd>
              </div>
              <div>
                <dt>Monthly housing + service</dt>
                <dd>{usd(econ.monthlyRent)}</dd>
              </div>
              <div>
                <dt>Sponsor capital at risk</dt>
                <dd>{compact(model.sponsorEquity)} equity</dd>
              </div>
              <div>
                <dt>Stabilized NOI</dt>
                <dd>{compact(model.rentalNoi)}</dd>
              </div>
              <div>
                <dt>Value @ {econ.capRate}%</dt>
                <dd>{compact(model.rentalValue)}</dd>
              </div>
            </dl>
            <button
              aria-pressed={mode === "rental"}
              onClick={() => setMode("rental")}
            >
              Underwrite rental
            </button>
          </article>
          <article className={mode === "sale" ? "selected" : ""}>
            <span>MODEL B</span>
            <h2>Sell homes; retain the land</h2>
            <p>
              Buyers acquire a financeable leasehold condominium or residence
              interest. The sponsor retains the land, collects ground rent and
              manages the separately budgeted service association.
            </p>
            <dl>
              <div>
                <dt>Illustrative purchase</dt>
                <dd>{usd(econ.salePrice)}</dd>
              </div>
              <div>
                <dt>Monthly service + ground</dt>
                <dd>{usd(econ.serviceFee + econ.groundRent)}</dd>
              </div>
              <div>
                <dt>Gross home sales</dt>
                <dd>{compact(model.grossSales)}</dd>
              </div>
              <div>
                <dt>Sellout profit</dt>
                <dd>{compact(model.saleProfit)}</dd>
              </div>
              <div>
                <dt>Retained ground value</dt>
                <dd>{compact(model.groundValue)}</dd>
              </div>
            </dl>
            <button
              aria-pressed={mode === "sale"}
              onClick={() => setMode("sale")}
            >
              Underwrite home sales
            </button>
          </article>
        </section>
        <div className="structure-warning">
          <Info />
          <p>
            <strong>Structure before marketing.</strong> In California, selling
            five or more subdivision interests generally requires a DRE public
            report, recorded governing documents, an adequate HOA budget and
            reserves. “Sponsor owns land, buyer owns the home” must be formed as
            a counsel-designed ground lease/condominium structure that mortgage
            lenders will accept. The service fee shown here funds hospitality
            and baseline wellness response—not unlimited ADL care or a
            lifetime-care promise.
          </p>
        </div>

        <section className="dev-summary">
          <article>
            <span>Total development cost</span>
            <strong>{compact(model.totalCost)}</strong>
            <small>{usd(model.totalCost / totalHomes)}/home</small>
          </article>
          <article>
            <span>
              {mode === "rental" ? "Stabilized NOI" : "Sellout profit"}
            </span>
            <strong>
              {compact(mode === "rental" ? model.rentalNoi : model.saleProfit)}
            </strong>
            <small>
              {mode === "rental"
                ? `${pct((model.rentalNoi / model.totalCost) * 100)} yield on cost`
                : `${pct((model.saleProfit / model.totalCost) * 100)} on cost`}
            </small>
          </article>
          <article>
            <span>Service budget coverage</span>
            <strong>{model.serviceCoverage.toFixed(2)}×</strong>
            <small>{usd(model.breakEvenService)}/home/mo break-even</small>
          </article>
          <article>
            <span>Payroll / FTE</span>
            <strong>{compact(model.payroll)}</strong>
            <small>
              {staff.reduce((s, r) => s + r.fte, 0).toFixed(1)} FTE · burden
              included
            </small>
          </article>
        </section>

        <section className="ledger-shell">
          <aside className="assumption-panel">
            <div className="dev-panel-title">
              <Calculator />
              <div>
                <strong>Live assumptions</strong>
                <small>Every input drives the ledger</small>
              </div>
            </div>
            <h3>Homes by phase</h3>
            {phaseHomes.map((n, i) => (
              <NumberField
                key={i}
                label={`Phase ${i + 1} homes`}
                value={n}
                onChange={(v) =>
                  setPhaseHomes(
                    (p) =>
                      p.map((x, j) => (j === i ? Math.max(1, v) : x)) as Phase,
                  )
                }
              />
            ))}
            <h3>Product + buildings</h3>
            <NumberField
              label="Average home GSF"
              value={costs.avgHomeGsf}
              step={10}
              onChange={(v) => setCost("avgHomeGsf", v)}
            />
            <NumberField
              label="Residential hard cost"
              value={costs.homeHardPsf}
              step={5}
              suffix="$/GSF"
              onChange={(v) => setCost("homeHardPsf", v)}
            />
            <NumberField
              label="Commons GSF"
              value={costs.commonsGsf}
              step={1000}
              onChange={(v) => setCost("commonsGsf", v)}
            />
            <NumberField
              label="Commons hard cost"
              value={costs.commonsHardPsf}
              step={5}
              suffix="$/GSF"
              onChange={(v) => setCost("commonsHardPsf", v)}
            />
            <h3>Fixed development uses · $M</h3>
            {(
              [
                ["land", "Land purchase"],
                ["closing", "Closing + diligence"],
                ["grading", "Grading"],
                ["roads", "Roads + fire access"],
                ["wetUtilities", "Wet utilities"],
                ["landscape", "Landscape + recreation"],
                ["resilience", "Energy + resilience"],
                ["entitlement", "Entitlement + CEQA"],
                ["ownerCosts", "Owner costs"],
                ["commonFfe", "Common FF&E"],
                ["mobility", "Mobility fleet"],
                ["marketingOpening", "Marketing + opening"],
                ["recruiting", "Recruitment"],
                ["operatingReserve", "Opening reserve"],
                ["finance", "Finance + interest"],
              ] as [keyof Costs, string][]
            ).map(([k, l]) => (
              <NumberField
                key={k}
                label={l}
                value={costs[k]}
                step={0.1}
                suffix="$M"
                onChange={(v) => setCost(k, v)}
              />
            ))}
            <h3>Variable development uses</h3>
            <NumberField
              label="Architecture + engineering"
              value={costs.aePct}
              step={0.1}
              suffix="%"
              onChange={(v) => setCost("aePct", v)}
            />
            <NumberField
              label="Permits / home"
              value={costs.permitsPerHome}
              step={1000}
              suffix="$"
              onChange={(v) => setCost("permitsPerHome", v)}
            />
            <NumberField
              label="Technology / home"
              value={costs.techPerHome}
              step={500}
              suffix="$"
              onChange={(v) => setCost("techPerHome", v)}
            />
            <NumberField
              label="Contingency"
              value={costs.contingencyPct}
              step={0.5}
              suffix="%"
              onChange={(v) => setCost("contingencyPct", v)}
            />
            <NumberField
              label="Developer fee"
              value={costs.developerFeePct}
              step={0.5}
              suffix="%"
              onChange={(v) => setCost("developerFeePct", v)}
            />
            <h3>
              {mode === "rental" ? "Rental economics" : "Sale + ground lease"}
            </h3>
            {mode === "rental" ? (
              <>
                <NumberField
                  label="Monthly rent"
                  value={econ.monthlyRent}
                  step={100}
                  suffix="$"
                  onChange={(v) => setE("monthlyRent", v)}
                />
                <NumberField
                  label="Ancillary blend"
                  value={econ.ancillaryRent}
                  step={50}
                  suffix="$/mo"
                  onChange={(v) => setE("ancillaryRent", v)}
                />
                <NumberField
                  label="Occupancy"
                  value={econ.occupancy}
                  max={100}
                  suffix="%"
                  onChange={(v) => setE("occupancy", v)}
                />
                <NumberField
                  label="Value cap rate"
                  value={econ.capRate}
                  step={0.25}
                  suffix="%"
                  onChange={(v) => setE("capRate", v)}
                />
              </>
            ) : (
              <>
                <NumberField
                  label="Average sale price"
                  value={econ.salePrice}
                  step={25000}
                  suffix="$"
                  onChange={(v) => setE("salePrice", v)}
                />
                <NumberField
                  label="Service / HOA fee"
                  value={econ.serviceFee}
                  step={50}
                  suffix="$/mo"
                  onChange={(v) => setE("serviceFee", v)}
                />
                <NumberField
                  label="Ground rent"
                  value={econ.groundRent}
                  step={25}
                  suffix="$/mo"
                  onChange={(v) => setE("groundRent", v)}
                />
                <NumberField
                  label="Selling costs"
                  value={econ.salesCostPct}
                  step={0.25}
                  suffix="%"
                  onChange={(v) => setE("salesCostPct", v)}
                />
                <NumberField
                  label="Sales pace"
                  value={econ.absorption}
                  step={0.25}
                  min={0.25}
                  suffix="homes/mo"
                  onChange={(v) => setE("absorption", v)}
                />
                <NumberField
                  label="Buyer down payment"
                  value={econ.buyerDownPct}
                  step={5}
                  max={100}
                  suffix="%"
                  onChange={(v) => setE("buyerDownPct", v)}
                />
                <NumberField
                  label="Buyer mortgage rate"
                  value={econ.mortgageRate}
                  step={0.125}
                  suffix="%"
                  onChange={(v) => setE("mortgageRate", v)}
                />
                <NumberField
                  label="Buyer mortgage term"
                  value={econ.mortgageTerm}
                  step={5}
                  suffix="years"
                  onChange={(v) => setE("mortgageTerm", v)}
                />
                <NumberField
                  label="Owner insurance"
                  value={econ.ownerInsuranceMonth}
                  step={25}
                  suffix="$/mo"
                  onChange={(v) => setE("ownerInsuranceMonth", v)}
                />
              </>
            )}
            <h3>Sponsor capital stack</h3>
            <NumberField
              label="Construction debt / cost"
              value={econ.constructionDebtPct}
              step={5}
              max={100}
              suffix="%"
              onChange={(v) => setE("constructionDebtPct", v)}
            />
          </aside>

          <div className="dev-workspace">
            <section className="dev-card" id="ledger">
              <div className="dev-card-heading">
                <div>
                  <p>Sources & uses</p>
                  <h2>Component development ledger</h2>
                </div>
                <FileSpreadsheet />
              </div>
              <div
                className="ledger-table detailed-ledger"
                tabIndex={0}
                aria-label="Scrollable component development cost ledger"
              >
                <div className="ledger-head">
                  <span>Cost component</span>
                  <span>Basis</span>
                  <span>Timing</span>
                  <span>Amount</span>
                  <span>%</span>
                </div>
                {model.costRows.map(([name, amount, basis, timing]) => (
                  <div className="ledger-row" key={name}>
                    <span>
                      <b>{name}</b>
                    </span>
                    <span>{basis}</span>
                    <span>{timing}</span>
                    <span>{usd(amount)}</span>
                    <span>{pct((amount / model.totalCost) * 100)}</span>
                  </div>
                ))}
                <div className="ledger-total">
                  <span>Total development cost</span>
                  <strong>{usd(model.totalCost)}</strong>
                  <b>100%</b>
                </div>
              </div>
              <p className="dev-note">
                <Info />
                The starting hard-cost inputs bracket CBRE’s 2026 average of
                $364/GSF total development cost and $388,830/revenue unit, but
                this amenity-rich low-rise concept carries more site and
                common-area scope. Replace every allowance with civil
                quantities, contractor pricing and actual impact-fee estimates
                before land goes hard.
              </p>
            </section>

            <section className="dev-card">
              <div className="dev-card-heading">
                <div>
                  <p>Model consequence</p>
                  <h2>
                    {mode === "rental"
                      ? "Rental value waterfall"
                      : "Home-sale + retained-land waterfall"}
                  </h2>
                </div>
                <Scale />
              </div>
              {mode === "rental" ? (
                <div className="waterfall-grid">
                  <article>
                    <span>Occupied revenue</span>
                    <strong>{compact(model.rentalRevenue)}</strong>
                    <small>
                      {totalHomes} homes × {econ.occupancy}% ×{" "}
                      {usd(econ.monthlyRent + econ.ancillaryRent)}/mo
                    </small>
                  </article>
                  <article>
                    <span>Operating cost</span>
                    <strong>({compact(model.rentalOpex)})</strong>
                    <small>
                      Payroll, hospitality, reserves, tax + marketing
                    </small>
                  </article>
                  <article>
                    <span>NOI</span>
                    <strong>{compact(model.rentalNoi)}</strong>
                    <small>
                      {pct((model.rentalNoi / model.rentalRevenue) * 100)}{" "}
                      margin
                    </small>
                  </article>
                  <article>
                    <span>Stabilized value</span>
                    <strong>{compact(model.rentalValue)}</strong>
                    <small>NOI ÷ {econ.capRate}% cap</small>
                  </article>
                </div>
              ) : (
                <>
                  <div className="waterfall-grid">
                    <article>
                      <span>Gross home sales</span>
                      <strong>{compact(model.grossSales)}</strong>
                      <small>
                        {totalHomes} × {usd(econ.salePrice)}
                      </small>
                    </article>
                    <article>
                      <span>Sales friction</span>
                      <strong>({compact(model.sellingCosts)})</strong>
                      <small>
                        {econ.salesCostPct}% commissions, marketing + closing
                      </small>
                    </article>
                    <article>
                      <span>Sellout profit</span>
                      <strong>{compact(model.saleProfit)}</strong>
                      <small>after full development cost</small>
                    </article>
                    <article>
                      <span>Retained land value</span>
                      <strong>{compact(model.groundValue)}</strong>
                      <small>
                        {compact(model.annualGroundRent)}/yr ÷{" "}
                        {econ.groundCapRate}%
                      </small>
                    </article>
                  </div>
                  <div className="buyer-carry">
                    <div>
                      <span>Illustrative buyer cash down</span>
                      <strong>{usd(model.buyerDownPayment)}</strong>
                      <small>
                        {econ.buyerDownPct}% down · {usd(model.buyerLoan)}{" "}
                        leasehold mortgage
                      </small>
                    </div>
                    <div>
                      <span>Mortgage principal + interest</span>
                      <strong>{usd(model.buyerPrincipalInterest)}/mo</strong>
                      <small>
                        {econ.mortgageRate}% · {econ.mortgageTerm} years
                      </small>
                    </div>
                    <div>
                      <span>All-in monthly housing carry</span>
                      <strong>{usd(model.buyerMonthlyCarry)}/mo</strong>
                      <small>
                        P&amp;I + estimated tax + insurance + service + ground
                        rent
                      </small>
                    </div>
                  </div>
                </>
              )}
              <div className="compare-foot">
                <strong>Recurring sponsor income:</strong>{" "}
                {mode === "rental"
                  ? `${compact(model.rentalNoi)} NOI before debt service`
                  : `${compact(model.sponsorRecurring)}/yr ground rent + management fee`}
                <span>
                  {mode === "sale"
                    ? `Illustrative sellout: ${model.selloutMonths.toFixed(0)} months at ${econ.absorption} homes/month. Retained land value is not added to sellout cash profit.`
                    : "The sponsor must finance and retain the entire real-estate basis through lease-up."}
                </span>
              </div>
              <div className="capital-stack">
                <span>
                  Total uses <b>{compact(model.totalCost)}</b>
                </span>
                <span>
                  Illustrative construction loan{" "}
                  <b>{compact(model.constructionLoan)}</b>
                </span>
                <span>
                  Sponsor equity before deposits / presales{" "}
                  <b>{compact(model.sponsorEquity)}</b>
                </span>
              </div>
            </section>

            <section className="dev-card" id="staff">
              <div className="dev-card-heading">
                <div>
                  <p>Annual people cost</p>
                  <h2>Editable staffing ledger</h2>
                </div>
                <Users />
              </div>
              <div className="staff-ledger">
                <div className="staff-head">
                  <span>Role</span>
                  <span>Department</span>
                  <span>FTE</span>
                  <span>Salary</span>
                  <span>Loaded annual</span>
                </div>
                {staff.map((row, i) => (
                  <div className="staff-row" key={row.role}>
                    <b>{row.role}</b>
                    <span>{row.department}</span>
                    <input
                      aria-label={`${row.role} FTE`}
                      type="number"
                      step=".5"
                      value={row.fte}
                      onChange={(e) =>
                        setStaff((s) =>
                          s.map((x, j) =>
                            j === i
                              ? {
                                  ...x,
                                  fte: Math.max(0, Number(e.target.value)),
                                }
                              : x,
                          ),
                        )
                      }
                    />
                    <input
                      aria-label={`${row.role} salary`}
                      type="number"
                      step="1000"
                      value={row.salary}
                      onChange={(e) =>
                        setStaff((s) =>
                          s.map((x, j) =>
                            j === i
                              ? {
                                  ...x,
                                  salary: Math.max(0, Number(e.target.value)),
                                }
                              : x,
                          ),
                        )
                      }
                    />
                    <strong>
                      {usd(
                        row.fte * row.salary * (1 + econ.payrollBurden / 100),
                      )}
                    </strong>
                  </div>
                ))}
              </div>
              <div className="staff-controls">
                <NumberField
                  label="Payroll taxes + benefits"
                  value={econ.payrollBurden}
                  step={1}
                  suffix="%"
                  onChange={(v) => setE("payrollBurden", v)}
                />
                <div>
                  <span>Total base payroll</span>
                  <strong>{usd(model.payrollBase)}</strong>
                </div>
                <div>
                  <span>Total loaded payroll</span>
                  <strong>{usd(model.payroll)}</strong>
                </div>
              </div>
              <p className="dev-note">
                <Info />
                This baseline provides hospitality, wellness coordination and
                24/7 response—not unlimited personal care. Add licensed staffing
                and a separate care-tier rate when ADL assistance, medication
                management or memory care enters scope.
              </p>
            </section>

            <section className="dev-card">
              <div className="dev-card-heading">
                <div>
                  <p>Annual service budget</p>
                  <h2>What the $3K–$4K must actually pay for</h2>
                </div>
                <Landmark />
              </div>
              <div className="service-layout">
                <div className="service-inputs">
                  <NumberField
                    label="Service / HOA fee"
                    value={econ.serviceFee}
                    step={50}
                    suffix="$/home/mo"
                    onChange={(v) => setE("serviceFee", v)}
                  />
                  <NumberField
                    label="Residents / home"
                    value={econ.residentsPerHome}
                    step={0.05}
                    onChange={(v) => setE("residentsPerHome", v)}
                  />
                  <NumberField
                    label="Food / resident / day"
                    value={econ.foodPerResidentDay}
                    step={1}
                    suffix="$"
                    onChange={(v) => setE("foodPerResidentDay", v)}
                  />
                  <NumberField
                    label="Utilities / home / month"
                    value={econ.utilitiesPerHomeMonth}
                    step={25}
                    suffix="$"
                    onChange={(v) => setE("utilitiesPerHomeMonth", v)}
                  />
                  <NumberField
                    label="Common utilities / year"
                    value={econ.commonUtilities}
                    step={25000}
                    suffix="$"
                    onChange={(v) => setE("commonUtilities", v)}
                  />
                  <NumberField
                    label="Maintenance / building basis"
                    value={econ.maintenancePct}
                    step={0.1}
                    suffix="%"
                    onChange={(v) => setE("maintenancePct", v)}
                  />
                  <NumberField
                    label="Insurance / year"
                    value={econ.insurance}
                    step={25000}
                    suffix="$"
                    onChange={(v) => setE("insurance", v)}
                  />
                  <NumberField
                    label="Programming / home / month"
                    value={econ.programmingPerHomeMonth}
                    step={10}
                    suffix="$"
                    onChange={(v) => setE("programmingPerHomeMonth", v)}
                  />
                  <NumberField
                    label="Transport operations / year"
                    value={econ.transport}
                    step={25000}
                    suffix="$"
                    onChange={(v) => setE("transport", v)}
                  />
                  <NumberField
                    label="Software + admin / year"
                    value={econ.softwareAdmin}
                    step={25000}
                    suffix="$"
                    onChange={(v) => setE("softwareAdmin", v)}
                  />
                  <NumberField
                    label="Replacement reserve / home / month"
                    value={econ.reservePerHomeMonth}
                    step={25}
                    suffix="$"
                    onChange={(v) => setE("reservePerHomeMonth", v)}
                  />
                  <NumberField
                    label="Sponsor management fee"
                    value={econ.managementFeePct}
                    step={0.5}
                    suffix="% revenue"
                    onChange={(v) => setE("managementFeePct", v)}
                  />
                </div>
                <div className="service-ledger">
                  {[
                    ["Loaded payroll", model.payroll],
                    ["Food ingredients", model.food],
                    ["Utilities", model.utilities],
                    ["Repairs + maintenance", model.maintenance],
                    ["Insurance", econ.insurance],
                    ["Culture, fitness + programs", model.programming],
                    ["Transport operations", econ.transport],
                    ["Software + administration", econ.softwareAdmin],
                    ["Replacement reserve", model.reserves],
                    ["Sponsor management fee", model.management],
                  ].map(([n, a]) => (
                    <div key={String(n)}>
                      <span>{n}</span>
                      <strong>{usd(Number(a))}</strong>
                    </div>
                  ))}
                  <div className="service-total">
                    <span>Total service cost</span>
                    <strong>{usd(model.serviceCosts)}</strong>
                  </div>
                  <article
                    className={
                      model.serviceSurplus >= 0 ? "positive" : "negative"
                    }
                  >
                    <span>Service revenue</span>
                    <strong>{usd(model.serviceRevenue)}</strong>
                    <small>
                      {model.serviceSurplus >= 0 ? "+" : ""}
                      {usd(model.serviceSurplus)} surplus / (shortfall)
                    </small>
                  </article>
                  <article>
                    <span>Break-even monthly fee</span>
                    <strong>{usd(model.breakEvenService)}</strong>
                    <small>At current home count and cost assumptions</small>
                  </article>
                </div>
              </div>
            </section>
          </div>
        </section>

        <section className="phase-ledger-section" id="phasing">
          <div className="lot-heading">
            <div>
              <p>Editable capital draw map</p>
              <h2>Change the homes. Watch every phase move.</h2>
            </div>
            <p>
              Land, entitlement and 80% of the community heart are front-loaded.
              Home-driven costs follow the phase count. This is a planning
              allocation—not a contractor draw schedule.
            </p>
          </div>
          <div className="phase-v2-grid">
            {model.phaseData.map((p, i) => (
              <article key={p.phase}>
                <span>{p.phase}</span>
                <div className="phase-home-edit">
                  <input
                    aria-label={`${p.phase} homes`}
                    type="number"
                    value={p.homes}
                    onChange={(e) =>
                      setPhaseHomes(
                        (x) =>
                          x.map((n, j) =>
                            j === i ? Math.max(1, Number(e.target.value)) : n,
                          ) as Phase,
                      )
                    }
                  />
                  <small>homes</small>
                </div>
                <dl>
                  <div>
                    <dt>Phase uses</dt>
                    <dd>{compact(p.cost)}</dd>
                  </div>
                  <div>
                    <dt>Cost / home</dt>
                    <dd>{compact(p.cost / p.homes)}</dd>
                  </div>
                  <div>
                    <dt>Cumulative capital</dt>
                    <dd>{compact(p.cumulative)}</dd>
                  </div>
                  <div>
                    <dt>
                      {mode === "sale"
                        ? "Net home-sale proceeds"
                        : "Annual occupied revenue"}
                    </dt>
                    <dd>{compact(mode === "sale" ? p.sales : p.annualRent)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
          <div className="phase-reconcile">
            <strong>Phase allocation total</strong>
            <span>{usd(model.phaseData.reduce((s, p) => s + p.cost, 0))}</span>
            <small>
              {Math.abs(
                model.phaseData.reduce((s, p) => s + p.cost, 0) -
                  model.totalCost,
              ) < 10
                ? "Reconciles to the development ledger"
                : "Check allocation assumptions"}
            </small>
          </div>
        </section>

        <section className="lot-section compact-lot" id="lot">
          <div className="lot-heading">
            <div>
              <p>27-acre conceptual fit</p>
              <h2>The land stays the same. The capital path changes.</h2>
            </div>
            <p>
              The same courtyard buildings, commons, mandir, gardens and
              low-speed mobility loop can support either legal/economic model.
              Ownership boundaries, maintenance obligations and reserve
              components must be drawn before the public report.
            </p>
          </div>
          <div className="lot-plan">
            <svg
              viewBox="0 0 900 520"
              role="img"
              aria-label="Conceptual phased site plan"
            >
              <path
                className="lot-boundary"
                d="M90 78Q194 24 334 51T592 38Q785 48 830 174T790 427Q650 495 470 477T133 452Q48 329 90 78Z"
              />
              <path
                className="open-space"
                d="M90 78Q210 15 353 60L310 140Q169 132 138 245L67 218Q55 126 90 78ZM592 38Q785 48 830 174L730 194Q690 103 570 122Z"
              />
              <path
                className="lot-loop"
                d="M190 180Q340 115 505 139T705 251Q712 360 610 408T334 414Q195 389 168 296T190 180Z"
              />
              <g className="lot-buildings phase-a">
                <rect x="315" y="210" width="240" height="125" rx="20" />
                <rect x="185" y="195" width="110" height="63" rx="15" />
                <rect x="185" y="290" width="115" height="65" rx="15" />
              </g>
              <g className="lot-buildings phase-b">
                <rect x="585" y="197" width="105" height="65" rx="15" />
                <rect x="585" y="300" width="105" height="65" rx="15" />
              </g>
              <g className="lot-buildings phase-c">
                <rect x="278" y="386" width="110" height="49" rx="15" />
                <rect x="475" y="391" width="110" height="49" rx="15" />
              </g>
              <circle className="lot-commons" cx="435" cy="273" r="43" />
              <text x="435" y="270" textAnchor="middle">
                COMMONS
              </text>
              <text x="435" y="287" textAnchor="middle">
                DINING · MANDIR
              </text>
              <text x="160" y="95">
                OPEN SPACE + FIRE BUFFER
              </text>
              <text x="625" y="455">
                FUTURE / FLEX
              </text>
            </svg>
            <span>
              Illustrative only · not surveyed, engineered, entitled or a
              recorded condominium plan
            </span>
          </div>
          <div className="plan-ledger">
            <div className="dev-card-heading">
              <div>
                <p>Floor plan + building map</p>
                <h2>Every product has a place and a cost basis.</h2>
              </div>
              <Building2 />
            </div>
            <div className="plan-ledger-grid">
              <div
                className="unit-mix-table"
                tabIndex={0}
                aria-label="Scrollable conceptual home mix"
              >
                <div className="ledger-head">
                  <span>Home product</span>
                  <span>Count</span>
                  <span>Net area</span>
                  <span>Site relationship</span>
                </div>
                {[
                  [
                    "Accessible 1-bedroom",
                    Math.round(totalHomes * 0.45),
                    "780–850 SF",
                    "Courtyard buildings",
                  ],
                  [
                    "Accessible 2-bedroom",
                    Math.round(totalHomes * 0.3),
                    "1,000–1,150 SF",
                    "Quiet wings / upper floor",
                  ],
                  [
                    "Garden cottage",
                    Math.round(totalHomes * 0.15),
                    "1,150–1,300 SF",
                    "Outer loop + family stays",
                  ],
                  [
                    "Flexible wellness suite",
                    totalHomes -
                      Math.round(totalHomes * 0.45) -
                      Math.round(totalHomes * 0.3) -
                      Math.round(totalHomes * 0.15),
                    "550–700 SF",
                    "Closest to commons + clinic",
                  ],
                ].map((row) => (
                  <div className="ledger-row" key={String(row[0])}>
                    <span>
                      <b>{row[0]}</b>
                    </span>
                    <span>{row[1]}</span>
                    <span>{row[2]}</span>
                    <span>{row[3]}</span>
                  </div>
                ))}
              </div>
              <small className="swipe-cue">
                Swipe the home-mix table to see area and site placement →
              </small>
              <div className="mini-floorplan">
                <div>
                  <span>Representative accessible 1BR</span>
                  <strong>820 NSF · ~950 GSF</strong>
                </div>
                <svg
                  viewBox="0 0 530 360"
                  role="img"
                  aria-label="Representative accessible one-bedroom conceptual floor plan"
                >
                  <rect
                    className="mini-shell"
                    x="15"
                    y="15"
                    width="500"
                    height="330"
                  />
                  <path
                    className="mini-wall"
                    d="M15 192H210V15M210 127H345V15M345 215H515M345 127V345M210 280H345"
                  />
                  <rect
                    className="mini-balcony"
                    x="355"
                    y="226"
                    width="150"
                    height="106"
                  />
                  <rect
                    className="mini-kitchen"
                    x="225"
                    y="35"
                    width="105"
                    height="72"
                    rx="4"
                  />
                  <circle className="mini-turn" cx="117" cy="100" r="35" />
                  <circle className="mini-turn" cx="280" cy="215" r="35" />
                  <text x="110" y="65">
                    BEDROOM
                  </text>
                  <text x="275" y="70">
                    KITCHEN
                  </text>
                  <text x="95" y="245">
                    BATH
                  </text>
                  <text x="270" y="172">
                    DINING
                  </text>
                  <text x="423" y="160">
                    LIVING
                  </text>
                  <text x="425" y="285">
                    BALCONY
                  </text>
                  <text x="282" y="320">
                    PUJA / FLEX
                  </text>
                </svg>
                <small>
                  Concept only. The 950 GSF underwriting basis includes the home
                  plus allocated corridors, shafts, walls and shared
                  circulation. Architect must validate accessibility, structure,
                  egress and plumbing.
                </small>
              </div>
            </div>
          </div>
        </section>

        <section className="dev-source-section">
          <div>
            <Building2 />
            <p>
              <strong>Development-cost anchor</strong>
              <br />
              CBRE’s 2026 survey reports average senior-housing development cost
              of $388,830 per revenue unit and $364/GSF. This model exposes a
              higher-amenity local concept line by line.
            </p>
          </div>
          <div>
            <Users />
            <p>
              <strong>Payroll anchor</strong>
              <br />
              Roles begin from San Diego occupational wage evidence, then add a
              visible 30% planning burden. Recruiters must validate every shift
              and licensed role.
            </p>
          </div>
          <div>
            <Info />
            <p>
              <strong>Decision, not promise</strong>
              <br />
              This is a feasibility calculator, not a bid, appraisal, tax
              opinion, securities offering, reserve study, subdivision public
              report, care license or lender commitment.
            </p>
          </div>
          <p className="dev-sources">
            Primary references:{" "}
            <a
              href="https://www.cbre.com/insights/briefs/2026-senior-housing-development-costs"
              target="_blank"
              rel="noreferrer"
            >
              CBRE 2026 costs <ExternalLink />
            </a>{" "}
            ·{" "}
            <a
              href="https://www.bls.gov/regions/west/news-release/2026/occupationalemploymentandwages_sandiego_20260709.htm"
              target="_blank"
              rel="noreferrer"
            >
              BLS San Diego wages <ExternalLink />
            </a>{" "}
            ·{" "}
            <a
              href="https://www.dre.ca.gov/developers/publicreports.html"
              target="_blank"
              rel="noreferrer"
            >
              California DRE public reports <ExternalLink />
            </a>{" "}
            ·{" "}
            <a
              href="https://www.dre.ca.gov/Developers/HOA.html"
              target="_blank"
              rel="noreferrer"
            >
              DRE HOA budgets + reserves <ExternalLink />
            </a>
          </p>
        </section>
      </main>
    </div>
  );
}
